import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Icon } from '@/components/atoms/Icon';
import { Label } from '@/components/atoms/Label';
import styles from './Select.module.scss';
import type { SelectProps } from './Select.types';

/**
 * Select - Dropdown select component
 * Based on Radix UI Select with SCSS styling
 *
 * @example
 * <Select
 *   label="Status"
 *   options={[
 *     { label: 'All', value: 'all' },
 *     { label: 'Active', value: 'active' },
 *     { label: 'Completed', value: 'completed' }
 *   ]}
 *   value={status}
 *   onValueChange={setStatus}
 * />
 */
export function Select({
  options,
  value,
  defaultValue,
  placeholder = 'Select an option',
  onValueChange,
  disabled = false,
  label,
  required = false,
  className,
  fitContent = false,
}: SelectProps) {
  const selectId = React.useId();

  return (
    <div
      className={cn(
        styles.select__wrapper,
        fitContent && styles['select__wrapper--fitContent'],
        className
      )}
    >
      {label && (
        <Label htmlFor={selectId} required={required} disabled={disabled}>
          {label}
        </Label>
      )}

      <SelectPrimitive.Root
        value={value}
        defaultValue={defaultValue}
        onValueChange={onValueChange}
        disabled={disabled}
      >
        <SelectPrimitive.Trigger
          id={selectId}
          className={styles.select__trigger}
          aria-label={label || placeholder}
        >
          <SelectPrimitive.Value placeholder={placeholder} />
          <SelectPrimitive.Icon asChild>
            <Icon icon={ChevronDown} size="sm" className={styles.select__icon} />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>

        <SelectPrimitive.Portal>
          <SelectPrimitive.Content
            className={styles.select__content}
            position="popper"
            sideOffset={4}
          >
            <SelectPrimitive.Viewport className={styles.select__viewport}>
              {options.map((option) => (
                <SelectPrimitive.Item
                  key={option.value}
                  value={option.value}
                  className={styles.select__item}
                >
                  <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                  <SelectPrimitive.ItemIndicator className={styles.select__indicator}>
                    <Icon icon={Check} size="sm" />
                  </SelectPrimitive.ItemIndicator>
                </SelectPrimitive.Item>
              ))}
            </SelectPrimitive.Viewport>
          </SelectPrimitive.Content>
        </SelectPrimitive.Portal>
      </SelectPrimitive.Root>
    </div>
  );
}

Select.displayName = 'Select';
